import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Droppable } from 'react-beautiful-dnd';
import AddIcon from '@material-ui/icons/Add';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import Menu from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Menu';
import MenuItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/MenuItem';
import Tabs from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Tabs';
import useUserContext from 'Context/UserContext';
import { pickStyles } from 'Helpers/Affordances';
import fr from 'facet-rest';
import { dataAttrs } from 'Helpers/Testing';
import ColumnTab from './ColumnTab';

const styles = theme => ({
  root: {
    display: 'flex',
    padding: theme.spacing(0, 2),
    paddingTop: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.main,
  },
  tabs: {
    minHeight: 0,
  },
  tabsIndicator: {
    display: 'none',
  },
  tab: {
    minHeight: theme.spacing(4),
    color: theme.palette.common.white,
  },
  tabActive: {
    backgroundColor: theme.palette.common.white,
    borderTopLeftRadius: theme.radius.sm,
    borderTopRightRadius: theme.radius.sm,
  },
  newTabActiveColors: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.facet.accentBlue11,
    borderTopLeftRadius: theme.radius.sm,
    borderTopRightRadius: theme.radius.sm,
  },
  tabCloseButtonWhite: {
    color: theme.palette.common.white,
    padding: theme.spacing(0.75),
  },
  openButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
  openButtonWhite: {
    color: theme.palette.common.white,
  },
  tabScrollButtons: {
    color: theme.palette.common.white,
  },
});

const funcIfMoreThanOneTab = (func, openTabNames) => {
  if (openTabNames.length === 1) {
    return null;
  }

  return func;
};

const ColumnTabs = ({
  activeTabName,
  className,
  classes,
  closeTab,
  columnConfiguration,
  columnIndex,
  layoutResource,
  openTab,
  openTabNames,
  moveTabToColumn,
  moveTabToNewColumn,
  setActiveTab,
  tabRegistry,
  popOutTab,
}) => {
  const { userResource } = useUserContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const availableTabs = Object.keys(tabRegistry);
  const colCount = layoutResource.attr('columns').resources().length();

  const containerStyles = pickStyles(userResource, {
    [fr.relationships.default.name]: [classes.root, className],
  });

  const scrollButtonClasses = pickStyles(userResource, {
    [fr.relationships.default.name]: [classes.tabScrollButtons],
  });

  return (
    <div
      className={containerStyles}
    >
      <Droppable
        direction="horizontal"
        droppableId={`columnIndex.${columnIndex}`}
      >
        {provided => (
          <Tabs
            classes={{
              root: classes.tabs,
              indicator: classes.tabsIndicator,
              scrollButtons: scrollButtonClasses,
            }}
            onChange={(evt, v) => {
              setActiveTab(columnIndex, v);
            }}
            ref={provided.innerRef}
            scrollButtons="auto"
            value={activeTabName}
            variant="scrollable"
            {...provided.droppableProps}
          >
            {columnConfiguration.attr('tabs')
              .resources()
              .list()
              .filter(tabResource => tabRegistry[tabResource.attr('name')])
              .map((tabResource, tabIndex) => {
                const tabName = tabResource.attr('name');
                const tabLabel = tabRegistry[tabName].label;
                const isActiveTab = tabName === activeTabName;
                const columnTabStyles = pickStyles(userResource, {
                  [fr.relationships.default.name]: [classes.tab, { [classes.newTabActiveColors]: isActiveTab }],
                });

                const contextMenuItems = [
                  {
                    disabled: columnIndex === 0,
                    label: 'Move Left',
                    onClick: moveTabToColumn(columnIndex, columnIndex - 1),
                  },
                  {
                    disabled: columnIndex === colCount - 1,
                    label: 'Move Right',
                    onClick: moveTabToColumn(columnIndex, columnIndex + 1),
                  },
                  {
                    disabled: columnConfiguration.attr('tabs').resources().length() === 1,
                    label: 'Split Right',
                    onClick: moveTabToNewColumn(columnIndex),
                  },
                  {
                    disabled: !isActiveTab,
                    label: 'Pop-out tab',
                    onClick: popOutTab,
                  },
                ];

                return (
                  <ColumnTab
                    classes={{
                      tabCloseButton: pickStyles(userResource, {
                        [fr.relationships.default.name]: [
                          classes.tabCloseButtonWhite,
                        ],
                      }),
                    }}
                    className={columnTabStyles}
                    contextMenuItems={contextMenuItems}
                    key={tabName}
                    label={tabLabel}
                    test={`tab label: ${tabLabel}`}
                    onClose={funcIfMoreThanOneTab(closeTab, openTabNames)}
                    tabIndex={tabIndex}
                    tabName={tabName}
                    value={tabName}
                    provided={provided}
                  />
                );
              })}
            {provided.placeholder}
          </Tabs>
        )}
      </Droppable>
      <IconButton
        className={
          pickStyles(userResource, {
            [fr.relationships.default.name]: [
              classes.openButton,
              classes.openButtonWhite,
            ],
          })
        }
        color="inherit"
        title="Open Tab"
        icon={AddIcon}
        onClick={(evt) => {
          setAnchorEl(evt.currentTarget);
        }}
        test="mission-control-open-tab-button"
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        getContentAnchorEl={null} // See https://github.com/mui-org/material-ui/issues/19245#issuecomment-620488016
        {...dataAttrs('mission-control-open-tab-menu')}
      >
        {availableTabs.map((tabName, i) => {
          const tabRegistryConfiguration = tabRegistry[tabName];

          return (
            <MenuItem
              disabled={openTabNames.includes(tabName)}
              key={`menu-item.${i}`} // eslint-disable-line react/no-array-index-key
              onClick={() => {
                openTab(tabName, columnIndex);
                setAnchorEl(null);
              }}
              test={`open tab: ${tabRegistryConfiguration.label}`}
            >
              {tabRegistryConfiguration.label}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

ColumnTabs.defaultProps = {
  className: '',
};

ColumnTabs.propTypes = {
  activeTabName: PropTypes.string.isRequired,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  closeTab: PropTypes.func.isRequired,
  columnConfiguration: PropTypes.object.isRequired,
  columnIndex: PropTypes.number.isRequired,
  layoutResource: PropTypes.object.isRequired,
  moveTabToColumn: PropTypes.func.isRequired,
  moveTabToNewColumn: PropTypes.func.isRequired,
  openTab: PropTypes.func.isRequired,
  openTabNames: PropTypes.array.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  tabRegistry: PropTypes.object.isRequired,
  popOutTab: PropTypes.func.isRequired,
};

export default withStyles(styles)(ColumnTabs);
